import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { getRecoil, setRecoil } from "recoil-nexus";
import styled from "styled-components";
import ApiController from "../../../controller/ApiController";
import LogController from "../../../controller/LogController";
import {
  EventType,
  WebStoreSelectorType,
} from "../../../controller/MasterController";
import { qrAuthState } from "../../../recoil/auth";
import { eventState } from "../../../recoil/event";
import { langText } from "../../../recoil/lang";
import { ThemeType } from "../../../recoil/theme";

interface EventSliderProps {
  storeList: WebStoreSelectorType | undefined;
  lang: number;
  setVisibleCouponListModal: React.Dispatch<SetStateAction<boolean>>;
  theme: ThemeType;
}

interface CheckInSliderProps {
  storeList: WebStoreSelectorType | undefined;
  lang: number;
  setVisibleCouponListModal: React.Dispatch<SetStateAction<boolean>>;
}

interface SliderProps {
  lang: number;
}

const EventSlide = ({
  storeList,
  lang,
  setVisibleCouponListModal,
}: CheckInSliderProps) => {
  const openEvent = () => {
    if (storeList && storeList.coupon_section.length > 0) {
      LogController.click({
        path: "StoreSelector(MainPage)",
        params: "event",
        params2: "이벤트 쿠폰 클릭",
      });

      setVisibleCouponListModal(true);
    }
  };

  return (
    <div className="slideBadge event" onClick={openEvent}>
      <div className="row gap">
        <img
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_slide_event.svg`}
          alt="event"
        />
        <h3>EVENT</h3>
      </div>
      <div className="row gap">
        <img
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_slide_coupon.svg`}
          alt="coupon"
        />
        <h3>{langText.slider_checkin_coupon[lang]}</h3>
        <img
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_arrow_right.svg`}
          alt="open"
        />
      </div>
    </div>
  );
};

const ChristmasSlide = ({ lang }: SliderProps) => {
  // API 이벤트리스트 불러오기
  const getEventList = async () => {
    const user_id = getRecoil(qrAuthState).user_id;
    const user_token_id = getRecoil(qrAuthState).user_token_id;

    try {
      const response = await ApiController.store.getEventList(
        user_id,
        user_token_id
      );

      if (response && response.data) {
        const oRes = response.data;
        const eventItem = oRes.data.find(
          (item: EventType) => item.event_type === 1
        );
        if (eventItem) {
          LogController.click({
            path: "StoreSelector(MainPage)",
            params: "event",
            params2: "이벤트 선물상자 클릭",
            index: eventItem.event_id,
          });

          setRecoil(eventState, {
            ...eventItem,
            visible: true,
            coupon: null,
          });
        }
      }
    } catch (err) {
      console.log("getAdverList err-->", err);
    }
  };

  return (
    <div className="slideBadge christmas" onClick={getEventList}>
      <div className="row">
        <img
          className="bell"
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_slide_bell.svg`}
          alt="bell"
        />
        <h3>CHRISTMAS EVENT</h3>
        <img
          className="cherry"
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_slide_cherry.svg`}
          alt="cherry"
        />
      </div>
      <div className="row gap">
        <img
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_slide_box.svg`}
          alt="box"
        />
        <h3>{langText.slider_christmas_coupon[lang]}</h3>
        <img
          src={`${process.env.PUBLIC_URL}/knockknock/svg/event/ic_arrow_right.svg`}
          alt="open"
        />
      </div>
    </div>
  );
};

const EventSlider = ({
  storeList,
  lang,
  setVisibleCouponListModal,
  theme,
}: EventSliderProps) => {
  const [count, setCount] = useState<number>(0);
  const [isBack, setIsBack] = useState<boolean>(false);
  const [front, setFront] = useState<string>("");
  const [back, setBack] = useState<string>("");

  const getSlide = (type: string) => {
    if (type === "christmas") {
      return <ChristmasSlide lang={lang} />;
    } else {
      return (
        <EventSlide
          storeList={storeList}
          lang={lang}
          setVisibleCouponListModal={setVisibleCouponListModal}
        />
      );
    }
  };

  useEffect(() => {
    setFront(theme.slide_coupons[0]);
    setBack(theme.slide_coupons[1 % theme.slide_coupons.length]);
    setIsBack(false);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (theme.slide_coupons.length > 1) {
        if (isBack) {
          setFront(
            theme.slide_coupons[(count + 1) % theme.slide_coupons.length]
          );
          setBack(theme.slide_coupons[count]);
        } else {
          setFront(theme.slide_coupons[count]);
          setBack(
            theme.slide_coupons[(count + 1) % theme.slide_coupons.length]
          );
        }
        setIsBack(!isBack);
        setCount((count + 1) % theme.slide_coupons.length);
      }
    }, 8000);
  }, [isBack]);

  const EventSlider = useMemo(() => {
    return (
      <Style>
        <div className="slideWrap">
          <div className={`flipContainer ${isBack && "active"}`}>
            <div className="back">{getSlide(back)}</div>
            <div className="front">{getSlide(front)}</div>
          </div>
        </div>
      </Style>
    );
  }, [storeList, lang, back, front, isBack, theme]);

  return EventSlider;
};
export default EventSlider;

const Style = styled.div`
  @font-face {
    font-family: "Jalnan2TTF";
    src: url("${process.env.PUBLIC_URL}/knockknock/font/Jalnan2TTF.ttf")
      format("truetype");
    font-weight: 400;
  }

  .slideWrap {
    width: 100%;
    height: 44px;
    padding: 0 14px;
  }

  .slideBadge {
    height: 100%;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 10px;

    img {
      height: 18px;
      object-fit: contain;
    }

    .row {
      gap: 0;
    }
    .row.gap {
      gap: 6px;
    }
    h3 {
      font-size: 10px;
      line-height: 18px;
      font-weight: 400;
      color: #fff;
      font-family: "Jalnan2TTF";
    }
  }
  .event {
    background: #46ae31;
  }
  .christmas {
    background: #ff3838;
    padding-left: 0;
    img.bell {
      margin-top: -8px;
    }
    img.cherry {
      margin-top: -12px;
    }
  }

  .flipContainer {
    perspective: 1000px;
    width: 100%;
    height: 100%;
    position: relative;

    .front,
    .back {
      width: 100%;
      height: 100%;
      backface-visibility: hidden;
      position: absolute;
      top: 0;
      left: 0;
      transition: transform 0.5s;
      transform-style: preserve-3d;
    }
    .front {
      z-index: 2;
    }
    .back {
      transform: rotateX(180deg);
      z-index: 1;
    }
  }

  .flipContainer.active {
    .front {
      z-index: 1;
      transform: rotateX(180deg);
    }
    .back {
      z-index: 2;
      transform: rotateX(0deg);
    }
  }
`;
